import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export default function MerchantCreate() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    legal_name: '',
    dba_name: '',
    ein: '',
    entity_type: '',
    state_of_formation: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/merchants`, formData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const newMerchantId = response.data.id;
      navigate(`/merchants/${newMerchantId}`);
    } catch (err) {
      if (err.response?.status === 409) {
        setError('A merchant with this EIN already exists.');
      } else {
        setError(err.response?.data?.detail || 'Failed to create merchant');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const entityTypeOptions = ['LLC', 'Corporation', 'Partnership', 'Sole Proprietor', 'S-Corp', 'C-Corp', 'Other'];
  const stateOptions = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>Create New Merchant</h1>
        <Link to="/merchants" style={styles.backLink}>← Back</Link>
      </div>

      <div style={styles.formContainer}>
        <form onSubmit={handleSubmit}>
          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.formGroup}>
            <label style={styles.label}>Legal Name *</label>
            <input
              type="text"
              name="legal_name"
              value={formData.legal_name}
              onChange={handleChange}
              required
              style={styles.input}
              placeholder="Company legal name"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>DBA Name</label>
            <input
              type="text"
              name="dba_name"
              value={formData.dba_name}
              onChange={handleChange}
              style={styles.input}
              placeholder="Doing Business As"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>EIN *</label>
            <input
              type="text"
              name="ein"
              value={formData.ein}
              onChange={handleChange}
              required
              style={styles.input}
              placeholder="XX-XXXXXXX"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Entity Type</label>
            <select
              name="entity_type"
              value={formData.entity_type}
              onChange={handleChange}
              style={styles.select}
            >
              <option value="">Select entity type</option>
              {entityTypeOptions.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>State of Formation</label>
            <select
              name="state_of_formation"
              value={formData.state_of_formation}
              onChange={handleChange}
              style={styles.select}
            >
              <option value="">Select state</option>
              {stateOptions.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              style={styles.textarea}
              placeholder="Additional notes"
              rows={4}
            />
          </div>

          <div style={styles.formActions}>
            <button
              type="submit"
              disabled={loading}
              style={styles.submitBtn}
            >
              {loading ? 'Creating...' : 'Create Merchant'}
            </button>
            <Link to="/merchants" style={styles.cancelBtn}>Cancel</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
  },
  header: {
    maxWidth: '800px',
    margin: '0 auto 30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backLink: {
    padding: '8px 16px',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: '4px',
    textDecoration: 'none',
    color: '#333',
    fontSize: '14px',
    fontWeight: '500',
  },
  formContainer: {
    maxWidth: '800px',
    margin: '0 auto',
    background: 'white',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  error: {
    padding: '12px 16px',
    background: '#f8d7da',
    color: '#721c24',
    borderRadius: '4px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    fontFamily: 'Arial, sans-serif',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    fontFamily: 'Arial, sans-serif',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    fontFamily: 'Arial, sans-serif',
    resize: 'vertical',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '30px',
  },
  submitBtn: {
    padding: '10px 24px',
    background: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  cancelBtn: {
    padding: '10px 24px',
    background: '#6c757d',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    textAlign: 'center',
  },
};
